import React, { useState } from 'react';
import moment from 'moment';
import cn from 'classnames';

import { useShareCommentsQuery } from 'src/api/queries/shareQueries';
import { useDeleteCommentMutation } from 'src/api/mutations/shareMutations';
import { ShareComment } from 'src/types/TShare';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import Button from 'src/components/Button';
import Shimmer from 'src/components/Shimmer';
import { Trash, Info, User } from 'src/common/icons';

import styles from './ShareCommentsPanel.module.scss';

interface ShareCommentsPanelProps {
  shareId: string;
}

export const ShareCommentsPanel: React.FC<ShareCommentsPanelProps> = ({ shareId }) => {
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { data: commentsResult, isLoading, refetch } = useShareCommentsQuery(shareId);

  const deleteCommentMutation = useDeleteCommentMutation(shareId, {
    onSuccess: () => {
      showSuccess('Comment deleted successfully');
      setDeletingId(null);
      refetch();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
      setDeletingId(null);
    },
  });

  const handleDeleteComment = (commentId: number) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      setDeletingId(commentId);
      deleteCommentMutation.mutate(commentId);
    }
  };

  const formatDate = (dateString: string): string => {
    return moment(dateString).format('MMM D, YYYY [at] h:mm A');
  };

  const comments = commentsResult?.data?.comments || [];

  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.loadingState}>
          <Shimmer w="100%" h="80px" radius="8px" />
          <Shimmer w="100%" h="80px" radius="8px" />
          <Shimmer w="100%" h="80px" radius="8px" />
        </div>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.emptyState}>
          <Info className={styles.emptyIcon} />
          <h3>No comments yet</h3>
          <p>Comments from share viewers will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          Comments ({comments.length})
        </h3>
      </div>

      <div className={styles.commentsList}>
        {comments.map((comment: ShareComment) => (
          <div key={comment.id} className={styles.commentCard}>
            <div className={styles.commentHeader}>
              <div className={styles.commenterInfo}>
                <div className={styles.avatar}>
                  <User className={styles.avatarIcon} />
                </div>
                <div className={styles.commenterDetails}>
                  <span className={styles.commenterName}>
                    {comment.commenter_name || 'Anonymous'}
                  </span>
                  {comment.commenter_email && (
                    <span className={styles.commenterEmail}>
                      {comment.commenter_email}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.commentMeta}>
                <span className={styles.timestamp}>
                  {formatDate(comment.created_utc)}
                </span>
                <Button
                  mode="tertiary"
                  size="small"
                  icon={<Trash />}
                  onClick={() => handleDeleteComment(comment.id)}
                  loading={deletingId === comment.id}
                  className={styles.deleteButton}
                  tooltip="Delete comment"
                />
              </div>
            </div>
            
            <div className={styles.commentBody}>
              <p className={styles.commentText}>{comment.comment}</p>
              {comment.asset_id && (
                <span className={styles.assetRef}>
                  Re: Asset {comment.asset_id.substring(0, 8)}...
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ShareCommentsPanel;

